import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import { useNodeResize } from './useNodeResize';
import { X } from 'lucide-react';

interface NoteNodeDataProps {
  text?: string;
  nodeWidth?: number;
  onUpdate?: (id: string, updates: any) => void;
  onDelete?: (id: string) => void;
  onWidthChange?: (id: string, width: number) => void;
}

export function NoteNode({ id, data: rawData, selected }: NodeProps) {
  const data = rawData as NoteNodeDataProps;
  const [text, setText] = useState(data.text || 'this is for ideas');

  const { nodeWidth, resizeHandleProps } = useNodeResize({
    width: data.nodeWidth as number | undefined,
    minWidth: 160,
    maxWidth: 600,
    onWidthChange: (w) => data.onWidthChange?.(id, w),
  });

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-grow: recalc height whenever text or width changes
  const adjustHeight = useCallback(() => {
    const el = textareaRef.current;
    if (el) {
      el.style.height = 'auto';
      el.style.height = el.scrollHeight + 'px';
    }
  }, []);

  useEffect(() => { adjustHeight(); }, [text, nodeWidth, adjustHeight]);

  const handleBlur = useCallback(() => {
    if (data.onUpdate) {
      data.onUpdate(id, { data: { ...data, text } });
    }
  }, [id, text, data]);

  return (
    <div
      className={`rounded shadow-md min-w-[160px] relative ${!nodeWidth ? 'max-w-[260px]' : ''} ${
        selected ? 'ring-2 ring-amber-400' : ''
      }`}
      style={{
        background: 'linear-gradient(135deg, #fef9c3 0%, #fef08a 100%)',
        transform: 'rotate(-1deg)',
        ...(nodeWidth ? { width: nodeWidth } : {}),
      }}
    >
      {/* Tape/pin decoration */}
      <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-8 h-4 rounded-sm opacity-60"
        style={{ background: 'linear-gradient(180deg, #d4a574 0%, #c9956c 100%)' }}
      />

      {/* Delete button */}
      {selected && (
        <button
          onClick={(e) => { e.stopPropagation(); data.onDelete?.(id); }}
          className="absolute -top-1 -right-1 z-10 bg-white border border-gray-300 rounded-full w-5 h-5 flex items-center justify-center text-gray-400 hover:text-red-500 hover:border-red-300 shadow-sm"
          aria-label="Delete note"
        >
          <X className="w-3 h-3" />
        </button>
      )}

      {/* Note content — always-editable textarea, click once to type */}
      <div className="p-4 pt-5 nodrag nopan">
        <textarea
          ref={textareaRef}
          className="w-full text-sm bg-transparent border-none outline-none ring-0 focus:outline-none focus:ring-0 p-0 placeholder-amber-400/60 cursor-text nowheel"
          style={{ fontStyle: 'italic', color: '#92400e', resize: 'none', overflow: 'hidden' }}
          value={text}
          rows={3}
          onChange={(e) => setText(e.target.value)}
          onBlur={handleBlur}
          placeholder="Write your idea..."
        />
      </div>

      {/* Folded corner effect */}
      <div
        className="absolute bottom-0 right-0 w-4 h-4"
        style={{
          background: 'linear-gradient(135deg, #fde047 0%, #fbbf24 50%, #d4a017 100%)',
          clipPath: 'polygon(100% 0, 100% 100%, 0 100%)',
          borderRadius: '0 0 4px 0',
        }}
      />

      <Handle type="target" position={Position.Left} className="!bg-amber-500 !w-3 !h-3" />
      <Handle type="source" position={Position.Right} className="!bg-amber-500 !w-3 !h-3" />
      <div {...resizeHandleProps} />
    </div>
  );
}
